import { useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import Input from '../components/ui/Input';
import LanguageSwitcher from '../components/ui/LanguageSwitcher';
import ThemeToggle from '../components/ui/ThemeToggle';
import { getDashboardPath } from '../utils/rolePaths';
import { isBookRedirect } from '../utils/bookingAuth';

const RegisterPage = () => {
  const { t } = useTranslation();
  const { registerClient, isAuthenticated, role } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', password: '' });
  const [loading, setLoading] = useState(false);

  if (isAuthenticated && role) {
    const target = redirect && role === 'CLIENT' ? redirect : getDashboardPath(role);
    return <Navigate to={target} replace />;
  }

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await registerClient(form);
      showToast(t('auth.accountCreated'), 'success');
      const target = redirect && user.role === 'CLIENT' ? redirect : getDashboardPath(user.role);
      navigate(target);
    } catch (err) {
      showToast(err.response?.data?.message || t('auth.registrationFailed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-zinc-50 dark:bg-zinc-950 px-4">
      <div className="absolute right-4 top-4 flex items-center gap-2">
        <LanguageSwitcher />
        <ThemeToggle />
      </div>
      <div className="w-full max-w-md rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-8">
        <h1 className="text-2xl font-semibold text-glow-400">{t('auth.registerTitle')}</h1>
        <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-500">{t('auth.registerSubtitle')}</p>
        {isBookRedirect(redirect) && (
          <p className="mt-2 rounded-lg border border-glow-600/30 bg-glow-600/10 px-3 py-2 text-sm text-glow-300">
            {t('auth.bookRegisterHint')}
          </p>
        )}

        <form onSubmit={handleSubmit} className="mt-8 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Input label={t('auth.firstName')} id="firstName" value={form.firstName} onChange={update('firstName')} required />
            <Input label={t('auth.lastName')} id="lastName" value={form.lastName} onChange={update('lastName')} required />
          </div>
          <Input label={t('auth.email')} id="email" type="email" value={form.email} onChange={update('email')} required />
          <Input label={t('auth.phone')} id="phone" value={form.phone} onChange={update('phone')} />
          <Input label={t('auth.password')} id="password" type="password" value={form.password} onChange={update('password')} required minLength={8} />
          <button type="submit" disabled={loading} className="w-full rounded-lg bg-glow-600 py-2.5 text-sm font-medium text-white hover:bg-glow-500 disabled:opacity-50">
            {loading ? t('auth.creatingAccount') : t('auth.getStarted')}
          </button>
        </form>

        <p className="mt-6 text-center text-sm text-zinc-600 dark:text-zinc-500">
          {t('auth.haveAccount')}{' '}
          <Link
            to={redirect ? `/login?redirect=${encodeURIComponent(redirect)}` : '/login'}
            className="text-glow-400 hover:underline"
          >
            {t('auth.signInLink')}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default RegisterPage;
